import React from 'react'
    import { Database, Bot, BarChart3, Globe, Shield, Zap, Brain, Settings } from 'lucide-react'

    const Services = () => {
      const serviceCategories = [
        {
          title: "Data Solutions",
          icon: <Database className="h-12 w-12 text-blue-600" />,
          services: [
            {
              name: "Data Engineering",
              description: "Robust data pipelines using modern ETL/ELT practices and open table formats for reliable AI foundations.",
              features: ["Modern ETL/ELT Pipelines", "Open Table Formats", "Real-time Processing", "Scalable Architecture"]
            },
            {
              name: "Data Quality & Governance",
              description: "Comprehensive frameworks ensuring data accuracy, security, and readiness for advanced analytics.",
              features: ["Data Quality Monitoring", "Governance Frameworks", "Compliance Management", "Data Lineage"]
            },
            {
              name: "Data Warehousing & Integration",
              description: "Scalable cloud data platforms creating a single source of truth for your enterprise.",
              features: ["Cloud Data Lakes", "Data Warehouses", "Lakehouse Architecture", "Enterprise Integration"]
            }
          ]
        },
        {
          title: "Generative AI & Machine Learning",
          icon: <Brain className="h-12 w-12 text-purple-600" />,
          services: [
            {
              name: "Custom AI Agent Development",
              description: "Bespoke AI agents automating specific business tasks with minimal human intervention.",
              features: ["Intelligent Customer Service", "Research Analysts", "Compliance Monitors", "Process Automation"]
            },
            {
              name: "Autonomous Workflows",
              description: "Teams of AI agents collaborating to execute complex, multi-step business processes.",
              features: ["Claims Processing", "Supply Chain Logistics", "Financial Due Diligence", "Multi-Agent Systems"]
            },
            {
              name: "LLM Integration & RAG",
              description: "Harness Large Language Models and Retrieval-Augmented Generation for proprietary data.",
              features: ["Custom LLM Integration", "RAG Systems", "Knowledge Management", "Intelligent Search"]
            }
          ]
        },
        {
          title: "Business Analytics & Intelligence",
          icon: <BarChart3 className="h-12 w-12 text-green-600" />,
          services: [
            {
              name: "Real-time Insights & Reporting",
              description: "Interactive dashboards providing immediate visibility into operations and AI performance.",
              features: ["Real-time Dashboards", "Performance Monitoring", "Custom Reports", "KPI Tracking"]
            },
            {
              name: "Customer & Market Analytics",
              description: "Predictive models analyzing consumer behavior and forecasting market trends.",
              features: ["Behavior Analysis", "Market Forecasting", "Predictive Models", "Customer Segmentation"]
            },
            {
              name: "Operational Efficiency",
              description: "Analyze and optimize autonomous workflows to reduce costs and maximize productivity.",
              features: ["Process Optimization", "Cost Analysis", "Efficiency Metrics", "Performance Tuning"]
            }
          ]
        },
        {
          title: "Digital Transformation",
          icon: <Globe className="h-12 w-12 text-indigo-600" />,
          services: [
            {
              name: "Cloud-native Architecture",
              description: "Scalable, cost-effective platforms serving as home for AI agents and user interfaces.",
              features: ["Serverless Architecture", "Microservices", "Cloud Migration", "Scalable Platforms"]
            },
            {
              name: "Custom Development",
              description: "Secure, user-friendly applications delivering AI insights directly to users.",
              features: ["Web Applications", "Mobile Apps", "API Development", "User Experience Design"]
            },
            {
              name: "Intelligent Workflows",
              description: "Dynamic workflows that learn and adapt over time, beyond basic automation.",
              features: ["Adaptive Workflows", "Process Intelligence", "Continuous Learning", "Smart Automation"]
            }
          ]
        }
      ]

      return (
        <div className="min-h-screen bg-white" data-component-path="src/pages/Services.jsx" data-component-line="1:4" data-component-id="src/pages/Services.jsx:1:4">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20" data-component-path="src/pages/Services.jsx" data-component-line="3:6" data-component-id="src/pages/Services.jsx:3:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Services.jsx" data-component-line="4:8" data-component-id="src/pages/Services.jsx:4:8">
              <div className="text-center" data-component-path="src/pages/Services.jsx" data-component-line="5:10" data-component-id="src/pages/Services.jsx:5:10">
                <h1 className="text-5xl font-bold text-gray-900 mb-6" data-component-path="src/pages/Services.jsx" data-component-line="6:12" data-component-id="src/pages/Services.jsx:6:12">
                  Our <span className="gradient-text" data-component-path="src/pages/Services.jsx" data-component-line="7:14" data-component-id="src/pages/Services.jsx:7:14">Services</span>
                </h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-component-path="src/pages/Services.jsx" data-component-line="9:12" data-component-id="src/pages/Services.jsx:9:12">
                  Comprehensive solutions designed to turn your data into intelligent, automated action across every aspect of your business.
                </p>
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <section className="py-20" data-component-path="src/pages/Services.jsx" data-component-line="16:6" data-component-id="src/pages/Services.jsx:16:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Services.jsx" data-component-line="17:8" data-component-id="src/pages/Services.jsx:17:8">
              <div className="space-y-20" data-component-path="src/pages/Services.jsx" data-component-line="18:10" data-component-id="src/pages/Services.jsx:18:10">
                {serviceCategories.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="bg-white" data-component-path="src/pages/Services.jsx" data-component-line="19:12" data-component-id="src/pages/Services.jsx:19:12">
                    {/* Category Header */}
                    <div className="text-center mb-12" data-component-path="src/pages/Services.jsx" data-component-line="21:14" data-component-id="src/pages/Services.jsx:21:14">
                      <div className="flex justify-center mb-4" data-component-path="src/pages/Services.jsx" data-component-line="22:16" data-component-id="src/pages/Services.jsx:22:16">
                        {category.icon}
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900" data-component-path="src/pages/Services.jsx" data-component-line="25:16" data-component-id="src/pages/Services.jsx:25:16">{category.title}</h2>
                    </div>

                    {/* Services Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-component-path="src/pages/Services.jsx" data-component-line="29:14" data-component-id="src/pages/Services.jsx:29:14">
                      {category.services.map((service, serviceIndex) => (
                        <div key={serviceIndex} className="bg-gray-50 rounded-xl p-8 card-hover" data-component-path="src/pages/Services.jsx" data-component-line="30:16" data-component-id="src/pages/Services.jsx:30:16">
                          <h3 className="text-xl font-semibold text-gray-900 mb-4" data-component-path="src/pages/Services.jsx" data-component-line="31:18" data-component-id="src/pages/Services.jsx:31:18">{service.name}</h3>
                          <p className="text-gray-600 mb-6" data-component-path="src/pages/Services.jsx" data-component-line="32:18" data-component-id="src/pages/Services.jsx:32:18">{service.description}</p>
                          
                          <div className="space-y-2" data-component-path="src/pages/Services.jsx" data-component-line="34:18" data-component-id="src/pages/Services.jsx:34:18">
                            {service.features.map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-center space-x-2" data-component-path="src/pages/Services.jsx" data-component-line="35:20" data-component-id="src/pages/Services.jsx:35:20">
                                <div className="w-2 h-2 bg-blue-600 rounded-full" data-component-path="src/pages/Services.jsx" data-component-line="36:22" data-component-id="src/pages/Services.jsx:36:22"></div>
                                <span className="text-sm text-gray-700" data-component-path="src/pages/Services.jsx" data-component-line="37:22" data-component-id="src/pages/Services.jsx:37:22">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Security & Compliance Section */}
          <section className="py-20 bg-gray-900 text-white" data-component-path="src/pages/Services.jsx" data-component-line="49:6" data-component-id="src/pages/Services.jsx:49:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Services.jsx" data-component-line="50:8" data-component-id="src/pages/Services.jsx:50:8">
              <div className="text-center mb-12" data-component-path="src/pages/Services.jsx" data-component-line="51:10" data-component-id="src/pages/Services.jsx:51:10">
                <Shield className="h-16 w-16 text-blue-400 mx-auto mb-6" data-component-path="src/pages/Services.jsx" data-component-line="52:12" data-component-id="src/pages/Services.jsx:52:12" />
                <h2 className="text-3xl font-bold mb-4" data-component-path="src/pages/Services.jsx" data-component-line="53:12" data-component-id="src/pages/Services.jsx:53:12">Security & Compliance First</h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-component-path="src/pages/Services.jsx" data-component-line="54:12" data-component-id="src/pages/Services.jsx:54:12">
                  We embed ironclad security and compliance into every solution we build.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-component-path="src/pages/Services.jsx" data-component-line="59:10" data-component-id="src/pages/Services.jsx:59:10">
                {['HIPAA', 'GDPR', 'CCPA', 'PCI-DSS'].map((compliance, index) => (
                  <div key={index} className="text-center p-6 bg-gray-800 rounded-lg" data-component-path="src/pages/Services.jsx" data-component-line="60:12" data-component-id="src/pages/Services.jsx:60:12">
                    <div className="text-2xl font-bold text-blue-400 mb-2" data-component-path="src/pages/Services.jsx" data-component-line="61:14" data-component-id="src/pages/Services.jsx:61:14">{compliance}</div>
                    <div className="text-gray-300" data-component-path="src/pages/Services.jsx" data-component-line="62:14" data-component-id="src/pages/Services.jsx:62:14">Compliant</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )
    }

    export default Services